import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, MoreHorizontal, Pencil, ArrowUpRight } from 'lucide-react';
import BulkSelectableTable from '../BulkSelectableTable';
import ListView from '../ListView';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

const COLLAPSED_KEY = 'bb.project.collapsedLists';

/** The lists a person folded in each project, kept on this device. */
export function useCollapsedLists(projectId) {
  const [all, setAll] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '{}') || {}; } catch { return {}; }
  });
  const collapsed = useMemo(() => new Set(all[projectId] || []), [all, projectId]);
  const toggle = useCallback((listId) => {
    setAll((prev) => {
      const ids = new Set(prev[projectId] || []);
      if (ids.has(listId)) ids.delete(listId); else ids.add(listId);
      const next = { ...prev, [projectId]: [...ids] };
      if (!ids.size) delete next[projectId];
      try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, [projectId]);
  return [collapsed, toggle];
}

/**
 * One list of a project on its Tasks tab: a header that folds it (name, open tasks,
 * the table's own controls, a "..." menu) and its tasks in the project's view, Table
 * or List, ending with the quick-add row. The project's view drives every list; a
 * list's own saved views open with "Open" (the list's page, ?section=).
 *
 * Without `name` it is the project's only list: no header, and the table puts its
 * controls into the page's toolbar (`toolbarTarget`).
 */
const ProjectListBlock = ({
  listId = null,
  name = null,
  tasks,
  viewType,
  visibleFields,
  customFieldDefs,
  projects,
  project,
  tableId,
  onTaskUpdate,
  onOptimisticUpdate,
  toolbarTarget,
  collapsed = false,
  onToggle,
  renaming = null,  // { value, onChange, onCommit, onCancel } while the name is edited
  onRename,
  onOpen,
  dropProps,
  dropActive = false,
}) => {
  const { t } = useTranslation();
  // Where the table puts its controls (select, grouping, columns, done tasks): the header.
  const [slot, setSlot] = useState(null);
  const keepSlot = useCallback((el) => { if (el) setSlot(el); }, []);
  // Rename from the menu: the name field keeps the focus the closing menu would take back.
  const renameNext = useRef(false);
  const openCount = tasks.filter((task) => task.status !== 'completed').length;
  const hasHeader = name != null;
  const quiet = 'md:opacity-0 md:group-hover/list:opacity-100 md:focus-within:opacity-100 transition-opacity';

  const body = viewType === 'list' ? (
    <ListView
      tasks={tasks}
      projects={projects}
      onUpdate={onTaskUpdate}
      prefillProjectId={project.id}
      prefillSectionId={listId}
      teamId={project.team_id || null}
      visibleFields={visibleFields}
      customFieldDefs={customFieldDefs}
      showEmpty={false}
    />
  ) : (
    <BulkSelectableTable
      key={tableId}
      tasks={tasks}
      projects={projects}
      onUpdate={onTaskUpdate}
      onBulkUpdate={onTaskUpdate}
      onOptimisticUpdate={onOptimisticUpdate}
      showProjectColumn={false}
      showSearch={false}
      showFilters={false}
      showGrouping
      showAddButton={false}
      showCompletedToggle
      emptyInlineAdd
      defaultGroupBy="none"
      defaultSortColumn="manual"
      prefillProjectId={project.id}
      prefillSectionId={listId}
      tableId={tableId}
      projectId={project.id}
      teamId={project.team_id || null}
      toolbarTarget={hasHeader ? slot : toolbarTarget}
    />
  );

  return (
    <section {...dropProps} data-testid="project-list"
      className={cn('group/list rounded-lg transition-colors', dropActive && 'ring-2 ring-amber-500/40 bg-amber-500/5')}>
      {hasHeader && (
        <div className="flex items-center gap-1 min-h-9 mb-1">
          {renaming ? (
            <div className="flex items-center gap-1.5 h-8 px-1 min-w-0">
              <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input
                autoFocus
                onFocus={(e) => e.target.select()}
                className="text-[14px] font-semibold bg-transparent border-b border-primary outline-none text-foreground w-48 max-w-full"
                value={renaming.value}
                onChange={(e) => renaming.onChange(e.target.value)}
                onBlur={renaming.onCommit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') renaming.onCommit();
                  if (e.key === 'Escape') renaming.onCancel();
                }}
              />
            </div>
          ) : (
            <button type="button" onClick={onToggle} aria-expanded={!collapsed} data-testid="project-list-toggle"
              className="flex items-center gap-1.5 h-8 px-1 -ml-1 rounded-md min-w-0 text-left hover:bg-accent/60 transition-colors">
              {collapsed
                ? <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
              <span className="truncate text-[14px] font-semibold text-foreground">{name}</span>
              <span className="text-[13px] text-muted-foreground tabular-nums flex-shrink-0">{openCount}</span>
            </button>
          )}
          <div ref={keepSlot} className={cn('ml-auto flex items-center', quiet)} />
          {(onRename || onOpen) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label={t('common.more')} title={t('common.more')}
                  className={cn('h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent', quiet)}>
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44"
                onCloseAutoFocus={(e) => { if (renameNext.current) { e.preventDefault(); renameNext.current = false; } }}>
                {onRename && (
                  <DropdownMenuItem onSelect={() => { renameNext.current = true; onRename(); }} className="text-[13px]">
                    <Pencil className="text-muted-foreground" />{t('common.rename')}
                  </DropdownMenuItem>
                )}
                {onOpen && (
                  <DropdownMenuItem onSelect={onOpen} className="text-[13px]">
                    <ArrowUpRight className="text-muted-foreground" />{t('common.open')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {!collapsed && body}
    </section>
  );
};

export default ProjectListBlock;
